import {
  ClientContextResponse,
  CustomerUpdatePayload,
} from '@ancon/wildcat-types'
import getSHA256Hash from '@ancon/wildcat-utils/hashing/getSHA256Hash'

import api from '../../../api'
import createAppAsyncThunk from '../../../store/createAppAsyncThunk'
import { ExtendedCustomerUserDetails } from '../types'
import { CompanyQRExtentCompanyMember } from '../../account/types'

import {
  clientContextCompanyMemberIdByCompanyIdSelector,
  clientContextCompanyTenantIdByCompanyIdSelector,
  clientContextCustomerIdSelector,
} from './clientContextSelectors'

export const fetchCustomerDetails = createAppAsyncThunk<
  ExtendedCustomerUserDetails,
  {
    customerId: string
    qrCodeExtent?: boolean
  }
>(
  'clientContext/fetchCustomerDetails',
  async ({ customerId, qrCodeExtent = false }) => {
    const response = await api.user.customers.get.details({
      customerId,
      'extent.QRCode': qrCodeExtent,
    })
    const { customer, qrCode } = response.data

    // Get customer email hash if email exists
    let emailHash = null
    if (customer.email) {
      emailHash = await getSHA256Hash(customer.email)
    }

    return { ...customer, emailHash, qrCode }
  },
)

export const fetchCompanyMemberDetails = createAppAsyncThunk<
  CompanyQRExtentCompanyMember,
  {
    companyId: string
    qrCodeExtent?: boolean
  }
>(
  'clientContext/fetchCompanyMemberDetails',
  async ({ companyId, qrCodeExtent = false }, { getState }) => {
    const memberId = clientContextCompanyMemberIdByCompanyIdSelector(
      getState(),
      companyId,
    )
    const tenantId = clientContextCompanyTenantIdByCompanyIdSelector(
      getState(),
      companyId,
    )

    const response = await api.user.companies.get.memberDetails({
      companyId: companyId!,
      memberId: memberId!,
      tenantId: tenantId!,
      'extent.companyQRCode': qrCodeExtent,
    })
    return response.data
  },
)

export const fetchClientContext = createAppAsyncThunk<
  ClientContextResponse | null,
  | {
      backgroundFetch?: boolean
    }
  | undefined
>('clientContext/fetchClientContext', async (_, { dispatch }) => {
  const response = await api.core.clientContext.get.details()

  const clientContext = response?.data

  const customer = clientContext?.customer

  if (customer?.email) {
    await dispatch(
      fetchCustomerDetails({
        customerId: customer.customerUserId,
        qrCodeExtent: true,
      }),
    )
  }

  return clientContext || null
})

export const updateCustomerDetails = createAppAsyncThunk<
  void,
  CustomerUpdatePayload
>('clientContext/updateCustomerDetails', async (customerData, { getState }) => {
  const customerId = clientContextCustomerIdSelector(getState())

  if (customerId) {
    await api.user.customers.put.byId(customerData, { customerId })
  }
})

export const activateCustomer = createAppAsyncThunk<void, { email: string }>(
  'auth/activateCustomer',
  async ({ email }, { getState }) => {
    const customerId = clientContextCustomerIdSelector(getState())

    if (customerId) {
      await api.user.customers.put.activate({ email }, { customerId })
    }
  },
)
